<template>
  <div class="pa3 pa4-l layout">
    <div v-if="expiredAgreement || minBidCountNotReached || expiredRating" class="icon-text f6 f5-l">
      <template v-if="expiredAgreement">
        <font-awesome-icon icon="exclamation-circle" class="red"/>
        <span class="red">Cогласование длится более 14 дней</span>
      </template>

      <template v-if="minBidCountNotReached">
        <font-awesome-icon icon="exclamation-circle" class="gold"/>
        <span class="gold">недостаточно {{ proc.minBidCount }} участников</span>
      </template>

      <template v-if="expiredRating">
        <font-awesome-icon icon="exclamation-circle" class="gold"/>
        <span class="gold">Не выставлен рейтинг более 14 дней с даты поставки</span>
      </template>
    </div>

    <div v-if="stages && stages.length > 1" class="stages">
      <span class="lh-copy text-primary ttu b">Этапы:</span>
      <div class="stages-items lh-copy">
        <div v-for="(stage, index) in stagesSorted" :key="index">
          <router-link  :to="{ name: 'procedure_card', params: { id: stage.procedureId } }"
            class="stages-item link text-primary underline f5" :class="{'b': stage.procedureId === proc.id}">
            {{ stage.procedureId }} ({{ stage.stage }})
          </router-link>
          <span v-if="index + 1 < stagesSorted.length" class="stages-delimeter text-secondary">
            <font-awesome-icon icon="caret-right"/>
          </span>
        </div>
      </div>
    </div>

    <div class="metrics-container">
      <div class="metrics">
        <div v-for="(metric, index) in metrics" :key="index" class="metric-card">
          <span class="db f5 mb2">{{ metric.title }}</span>
          <span class="db f4 fw4 text-accent">{{ metric.value }}</span>
        </div>
      </div>
      <div class="timeline">
        <template v-for="(item, index) in statusHistory">
          <span :key="index"
            class="timeline-pointer"
            :class="[inFuture(item.date) ? 'timeline-pointer-empty' : 'timeline-pointer-full']" >
          </span>
          <div :key="100 + index">
            <span class="db fw6">{{ item.eventName }}</span>
            <span class="db">{{ format(item.date) }}</span>
          </div>
        </template>
      </div>
    </div>

    <div class="participants-container">
      <div v-if="this.statuses[this.proc.stateId].code === 'closed'">
        <div class="text-primary ttu f6 b mb3">
          Победители
        </div>
        <div v-if="winners && winners.length">
          <div v-for="(winner, index) in winners" :key="index" class="lh-copy company mt3">
            <font-awesome-icon icon="trophy" class="text-accent f6"/>
            <span v-if="winner.externalSupplier" class="text-primary">{{ winner.title }}</span>
            <a v-else class="link text-primary underline" :href="companyLink(winner.companyId)" target="_blank">
              {{ winner.title }}
            </a>
            <span></span>
            <div v-if="winner.inn"><span class="text-secondary mr2 ttu f6">ИНН</span>{{ winner.inn }}</div>
            <div v-if="winner.externalSupplier" class="text-secondary f6">незарегистрирован в ТендерПро</div>
          </div>
        </div>
        <div v-else class="text-accent lh-copy company">
          <font-awesome-icon icon="exclamation-triangle" class="text-accent f6"/>
          <span>победитель не определен</span>
        </div>
      </div>
      <div v-if="participants && participants.length">
        <div class="text-primary ttu f6 b mb3">
          Участники
        </div>
        <div v-for="(participant, index) in participants" :key="index" class="lh-copy company mt3">
          <font-awesome-icon icon="user-tie" class="text-accent f6"/>
          <span v-if="participant.externalSupplier" class="text-primary">{{ participant.title }}</span>
          <a v-else class="link text-primary underline" :href="companyLink(participant.companyId)" target="_blank">
            {{ participant.title }}
          </a>
          <span></span>
          <div v-if="participant.externalSupplier" class="text-secondary f6">незарегистрирован в ТендерПро</div>
        </div>
      </div>
    </div>

    <div v-if="proc.canceledReason" class="flex flex-column lh-copy">
      <div class="text-primary ttu f6 b mb3">
        Отклонен
      </div>
      <div class="text-accent company">
        <font-awesome-icon icon="exclamation-triangle" class="text-accent f6"/>
        <span>{{ proc.canceledReason }}</span>
      </div>
    </div>

  </div>
</template>

<script>
import IconInCircle from '@/components/ui/IconInCircle.vue'
import { mapGetters } from 'vuex'
import { humanizeDuration } from '@/plugins/consts.js'
import { expiredAgreement, expiredRating, minBidCountNotReached } from '@/utils/procedure.js'
import { tproURL } from '@/utils/app.js'

const moment = require('moment')
moment.locale('ru')

export default {
  components: {
    IconInCircle
  },
  props: ['proc', 'journal', 'invites', 'winners', 'participants', 'stages'],
  computed: {
    ...mapGetters('companyInfo', ['managers']),
    ...mapGetters('procedure', ['participant', 'statuses']),
    statusHistory () {
      return this.journal.filter(i => [1, 2, 3, 4].includes(i.eventType))
    },
    duration () {
      // Если конкурс закрыт, то дительность рассчитывается от даты формирования до даты закрытия
      // если же не закрыт, то настоящего времени (т.е. он не закрыт и все еще длится)
      if (this.statusHistory && this.statusHistory.length) {
        const end = +this.statusHistory.slice(-1)[0].eventType === 4
          ? moment(this.statusHistory.slice(-1)[0].date)
          : moment()
        return humanizeDuration(moment(this.statusHistory[0].date), end)
      } else {
        return 0
      }
    },
    metrics () {
      const invitedByOrg = this.invites.filter(i => i.isOrg).length
      const invitedByTpro = this.invites.length - invitedByOrg
      return [
        { value: this.duration, title: 'Длительность' },
        { value: this.proc.viewedCount, title: 'Просмотров' },
        { value: this.proc.participantsCount, title: 'Участников зарегистр. в Тпро' },
        { value: this.proc.productCount, title: 'Товаров' },
        { value: invitedByTpro, title: 'Приглашено ТендерПро' },
        { value: invitedByOrg, title: 'Приглашено Организатором' }
      ]
    },
    expiredAgreement () {
      return expiredAgreement(this.proc)
    },
    expiredRating () {
      return expiredRating(this.proc)
    },
    minBidCountNotReached () {
      return minBidCountNotReached(this.proc)
    },
    stagesSorted () {
      return this.stages && this.stages.length > 0
        ? [...this.stages].sort((l, r) => l.stage - r.stage)
        : this.stages
    }
  },
  methods: {
    part (participantId) {
      return this.participant(participantId)
    },
    format (dt) {
      // return momentFromString(dt)?.format('LLL')
      return moment(dt)?.format('LLLL')
    },
    inFuture (date) {
      return moment(date).isSameOrAfter(moment())
    },
    companyLink (companyId) {
      return `${tproURL()}/view_other_company.shtml?companyid=${companyId}`
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  row-gap: 2.5rem;
  align-items: center;
}
.icon-text {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: .5rem;
  align-items: center;
}
.stages {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto;
  column-gap: 1rem;
  &-items {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
  &-item {
    padding: .25rem .5rem .25rem .5rem;
  }
  &-delimeter {
    padding: .25rem .5rem .25rem .5rem;
  }
}
@media screen and (min-width: 60em) {
  .stages {
    &-items {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }
    &-item {
      padding: .25rem .5rem .25rem .5rem;
    }
    &-delimeter {
      padding: .25rem .5rem .25rem .5rem;
    }
  }
}
.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  grid-template-rows: auto;
  justify-items: stretch;
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
}
.metric-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #334E68; // $primary-color;
  box-shadow: 0px 1px 3px 0px rgba(210,218,224,.8);
  padding: 1rem;
  border-radius: .2rem;
}
.metrics-container {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-column-gap: 3rem;
  grid-row-gap: 2rem;
}
@media screen and (min-width: 60em) {
  .metrics-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }
}
.timeline {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: repeat(auto-fill, 45px);
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
  align-items: start;
  box-shadow: 0px 1px 3px 0px rgba(210,218,224,.8);
  padding: 1rem;
  border-radius: .2rem;
  &-pointer {
    align-self: center;
    display: block;
    width: .75rem;
    height: .75rem;
    border-radius: 5rem;
    border: 1px solid #F9703E;
    overflow: hidden;
    &-empty {
      background-color: inherit;
    }
    &-full {
      background-color: #F9703E;
    }
  }
}
.procedure-details-list-subcontent {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  justify-items: stretch;
  grid-column-gap: 2rem;
  grid-row-gap: 2rem;
}
.company {
  display: grid;
  grid-template-columns: 25px 1fr;
  grid-template-rows: auto;
  align-items: center;
}
.participants-container {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-column-gap: 3rem;
  grid-row-gap: 2rem;
}
@media screen and (min-width: 60em) {
  .participants-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }
}
</style>
